import {
  Form,
  DatePicker,
  Table,
  ConfigProvider,
  Row,
  Col,
  Button,
  Tooltip,
  Select,
  Input,
  Card,
  notification,
} from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getOrderStatusReport } from '../../../../store/requests/reports';
import 'moment/locale/tr';
import trTR from 'antd/es/locale/tr_TR';
import { useSelector } from 'react-redux';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import {
  CSV_EXPORT_COLUMNS_ORDER_STATUS_REPORT,
  ORDER_STATUSES,
  debouncedDataLoadWaitMs,
} from 'helpers/constants';
import { DownloadOutlined } from '@ant-design/icons';

const { Option } = Select;
let lastQuery = false;

const Index = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [orderStatusReport, setOrderStatusReport] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const userRegions = useSelector((state) => state?.definitions?.userRegions);
  const userStores = useSelector((state) => state?.definitions?.userStores);
  const activeStore = useSelector((state) => state.auth?.user?.activeStore);
  const activeRegion = useSelector((state) => state.auth?.user?.activeRegion);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [stores, setStores] = useState([]);
  const [payloadData, setPayloadData] = useState(null);
  const [dates, setDates] = useState([]);
  const csvLink = useRef();
  const [downloadedData, setDownloadedData] = useState([]);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [30, 60, 90],
      size: 'small',
      current: 1,
      pageSize: 30,
    },
  });

  useEffect(() => {
    if (payloadData) {
      fetchData();
      return () => {
        lastQuery = null;
        setLoading(false);
      };
    }
  }, [JSON.stringify(tableParams)]);

  useEffect(() => {
    if (userStores.length > 0 && userRegions.length > 0) {
      let allStoresSelectedRegions = [];
    const findSelectedRegions = (id) => {
      const selectedRegion = userRegions.find((region) => region.id === id);
      if (selectedRegion) {
        allStoresSelectedRegions.push(...selectedRegion.children);
      }
    };

    if (activeRegion) {
      findSelectedRegions(activeRegion.id);
      setStores(allStoresSelectedRegions);
      setSelectedRegions([activeRegion.id]);
      form.setFieldsValue({ regionIds: [activeRegion.id] });
    }

    if (activeStore) {
      const selectStore = userStores.find(
        (store) => store?.id === activeStore?.id,
      );
      if (selectStore) {
        findSelectedRegions(selectStore.region?.id);
        setStores(allStoresSelectedRegions);
        setSelectedRegions([selectStore.region?.id]);
        setSelectedStores([activeStore.id]);
        form.setFieldsValue({
          regionIds: [selectStore.region?.id],
          storeIds: [activeStore.id],
        });
      }
    }
    }
  }, [activeRegion, activeStore, userStores, userRegions]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const getQueryString = () => {
    let queryString;
    queryString = `page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;

    return queryString;
  };

  useEffect(() => {
    if (userRegions.length < 1) {
      setStores(userStores);
    }
  }, [userRegions]);

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const handleChangeRegion = async (selected, options) => {
    let allStoresSelectedRegions = [];
    setSelectedRegions([]);

    if (selected.length === 0) {
      setSelectedStores([]);
      setStores([]);
      form.resetFields(['storeIds']);

      return;
    }

    setSelectedRegions(selected);
    selected.forEach((selectedRegion) => {
      const storesOfRegion =
        userRegions.find((region) => region.id === selectedRegion.value)
          ?.children || [];
      allStoresSelectedRegions.push(...storesOfRegion);
    });
    setStores(allStoresSelectedRegions);
  };

  const handleChangeStore = (selected) => {
    setSelectedStores(selected);
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getData(queryString, payloadData);
    }
  }

  const onFinish = (values) => {
    const url = 'page=0&size=30';
    const [startDate, endDate] = dates;
    let idsOfRegions = [];

    if (values.regionIds?.length > 0) {
      idsOfRegions = values.regionIds.map((region) => region.value ? region.value : region);
    }

    const payload = {
      startDate: startDate,
      endDate: endDate,
      customerOrderNumber: values.orderNo ? values.orderNo : '',
      customerName: values.customerName ? values.customerName : '',
      customerPhoneNumber: values.customerPhoneNumber
        ? values.customerPhoneNumber
        : '',
      orderStatus: values.orderStatus ? values.orderStatus : '',
      regionIds: values.regionIds?.length > 0 ? idsOfRegions : [],
      storeIds: values.storeIds?.length > 0 ? values.storeIds : [],
      platform: values.platform ? values.platform : '',
    };

    setPayloadData(payload);

    getData(url, payload);
  };

  const getData = (url, payload) => {
    setLoading(true);
    setOrderStatusReport([]);
    getOrderStatusReport(url, payload)
      .then((res) => {
        if (res.isFilteredData) {
          const { content, page } = res.data;
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page?.totalElements,
            },
          });
          setOrderStatusReport(content ?? []);
        } else {
          if (res.message) {
            notification.info({ message: res.message });
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onFinishFailed = () => {
    console.error('Error');
  };

  const columns = [
    {
      title: t('orderNumber'),
      dataIndex: 'customerOrderNumber',
      key: 'customerOrderNumber',
      sorter: (a, b) =>
        a.customerOrderNumber.localeCompare(b.customerOrderNumber),
      showSorterTooltip: false,
    },
    {
      title: t('platform'),
      dataIndex: 'platform',
      key: 'platform',
      sorter: (a, b) => a.platform.localeCompare(b.platform),
      showSorterTooltip: false,
    },
    {
      title: t('orderPlacementDate'),
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      sorter: (a, b) => {
        const dateA = new Date(a.deliveryDate);
        const dateB = new Date(b.deliveryDate);
        return dateA - dateB;
      },
      showSorterTooltip: false,
      render: (dataItem) => {
        if (!dataItem || !moment(dataItem).isValid()) {
          return '--';
        }
        return moment(dataItem).format('DD-MM-YYYY HH:mm');
      },
    },
    {
      title: t('region'),
      dataIndex: 'regionName',
      key: 'regionName',
      sorter: (a, b) => a.regionName.localeCompare(b.regionName),
      showSorterTooltip: false,
    },
    {
      title: t('storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      sorter: (a, b) => a.storeCode.localeCompare(b.storeCode),
      showSorterTooltip: false,
    },
    {
      title: t('store'),
      dataIndex: 'storeName',
      key: 'storeName',
      sorter: (a, b) => a.storeName.localeCompare(b.storeName),
      showSorterTooltip: false,
    },
    {
      title: t('customerFullName'),
      dataIndex: 'customerFullName',
      key: 'customerFullName',
      sorter: (a, b) => a.customerFullName?.localeCompare(b.customerFullName),
      showSorterTooltip: false,
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('customerId'),
      dataIndex: 'customerId',
      key: 'customerId',
      sorter: (a, b) => a.customerId - b.customerId,
      showSorterTooltip: false,
    },
    {
      title: t('customerPhoneNumber'),
      dataIndex: 'customerPhoneNumber',
      key: 'customerPhoneNumber',
      sorter: (a, b) =>
        a.customerPhoneNumber?.localeCompare(b.customerPhoneNumber),
      showSorterTooltip: false,
    },
    {
      title: t('orderStatus'),
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      sorter: (a, b) => a.orderStatus?.localeCompare(b.orderStatus),
      showSorterTooltip: false,
      render: (orderStatus) => (orderStatus ? t(orderStatus) : '-'),
    },
    {
      title: t('cancelReturnStatus'),
      dataIndex: 'cancelReturnStatus',
      key: 'cancelReturnStatus',
      sorter: (a, b) =>
        a.cancelReturnStatus?.localeCompare(b.cancelReturnStatus),
      showSorterTooltip: false,
    },
    {
      title: t('cancellationReason'),
      dataIndex: 'cancellationReason',
      key: 'cancellationReason',
      sorter: (a, b) =>
        a.cancellationReason?.localeCompare(b.cancellationReason),
      showSorterTooltip: false,
    },
    {
      title: t('nonDeliveryReason'),
      dataIndex: 'nonDeliveryReason',
      key: 'nonDeliveryReason',
      sorter: (a, b) => a.nonDeliveryReason?.localeCompare(b.nonDeliveryReason),
      showSorterTooltip: false,
    },
    {
      title: t('paymentType'),
      dataIndex: 'paymentType',
      key: 'paymentType',
      sorter: (a, b) => a.paymentType?.localeCompare(b.paymentType),
      showSorterTooltip: false,
    },
    {
      title: t('deliveryType'),
      dataIndex: 'storeStatus',
      key: 'storeStatus',
      sorter: (a, b) => a.storeStatus?.localeCompare(b.storeStatus),
      showSorterTooltip: false,
      render: (storeStatus) => (storeStatus ? t(storeStatus) : '-'),
    },
    {
      title: t('orderDeliveryDate'),
      dataIndex: 'orderDeliveryDate',
      key: 'orderDeliveryDate',
      sorter: (a, b) => {
        const dateA = new Date(a.deliveryDate);
        const dateB = new Date(b.deliveryDate);
        return dateA - dateB;
      },
      showSorterTooltip: false,
      render: (dataItem) => {
        if (!dataItem || !moment(dataItem).isValid()) {
          return '--';
        }
        return moment(dataItem).format('DD-MM-YYYY');
      },
    },
    {
      title: t('orderSlot'),
      dataIndex: 'timeSlot',
      key: 'timeSlot',
      sorter: (a, b) => a.timeSlot?.localeCompare(b.timeSlot),
      showSorterTooltip: false,
    },
    {
      title: t('collectionStartDate'),
      dataIndex: 'collectionStartDate',
      key: 'collectionStartDate',
      sorter: (a, b) => {
        const dateA = new Date(a.collectionStartDate);
        const dateB = new Date(b.collectionStartDate);
        return dateA - dateB;
      },
      showSorterTooltip: false,
      render: (dataItem) => {
        if (!dataItem || !moment(dataItem).isValid()) {
          return '--';
        }
        const formattedDate = moment
          .utc(dataItem)
          .add(3, 'hours')
          .format('DD-MM-YYYY HH:mm');
        return formattedDate;
      },
    },
    {
      title: t('collectionEndDate'),
      dataIndex: 'collectionEndDate',
      key: 'collectionEndDate',
      sorter: (a, b) => {
        const dateA = new Date(a.collectionEndDate);
        const dateB = new Date(b.collectionEndDate);
        return dateA - dateB;
      },
      showSorterTooltip: false,
      render: (dataItem) => {
        if (!dataItem || !moment(dataItem).isValid()) {
          return '--';
        }
        const formattedDate = moment
          .utc(dataItem)
          .add(3, 'hours')
          .format('DD-MM-YYYY HH:mm');
        return formattedDate;
      },
    },
    {
      title: t('collectionTime'),
      dataIndex: 'collectionTime',
      key: 'collectionTime',
      sorter: (a, b) => a.collectionTime - b.collectionTime,
      showSorterTooltip: false,
      render: (collectionTime) => (collectionTime ? collectionTime : '-'),
    },
    {
      title: t('courierDeliveryDate'),
      dataIndex: 'courierDeliveryDate',
      key: 'courierDeliveryDate',
      sorter: (a, b) => {
        const dateA = new Date(a.courierDeliveryDate);
        const dateB = new Date(b.courierDeliveryDate);
        return dateA - dateB;
      },
      showSorterTooltip: false,
      render: (dataItem) => {
        if (!dataItem || !moment(dataItem).isValid()) {
          return '--';
        }
        const formattedDate = moment
          .utc(dataItem)
          .add(3, 'hours')
          .format('DD-MM-YYYY HH:mm');
        return formattedDate;
      },
    },
    {
      title: t('customerDeliveryDate'),
      dataIndex: 'customerDeliveryDate',
      key: 'customerDeliveryDate',
      sorter: (a, b) => {
        const dateA = new Date(a.customerDeliveryDate);
        const dateB = new Date(b.customerDeliveryDate);
        return dateA - dateB;
      },
      showSorterTooltip: false,
      render: (dataItem) => {
        if (!dataItem || !moment(dataItem).isValid()) {
          return '--';
        }
        const formattedDate = moment
          .utc(dataItem)
          .add(3, 'hours')
          .format('DD-MM-YYYY HH:mm');
        return formattedDate;
      },
    },
    {
      title: t('deliveryTime'),
      dataIndex: 'deliveryTime',
      key: 'deliveryTime',
      sorter: (a, b) => a.deliveryTime - b.deliveryTime,
      showSorterTooltip: false,
      render: (deliveryTime) => (deliveryTime ? deliveryTime : '-'),
    },
    {
      title: t('totalPrice'),
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a.price - b.price,
      showSorterTooltip: false,
    },
    {
      title: t('refundedPrice'),
      dataIndex: 'refundedPrice',
      key: 'refundedPrice',
      sorter: (a, b) => a.refundedPrice - b.refundedPrice,
      showSorterTooltip: false,
    },
    {
      title: t('refundAmountForUnavailableProduct'),
      dataIndex: 'refundAmountForUnavailableProduct',
      key: 'refundAmountForUnavailableProduct',
      sorter: (a, b) =>
        a.refundAmountForUnavailableProduct -
        b.refundAmountForUnavailableProduct,
      showSorterTooltip: false,
    },
    {
      title: t('partialRefundAmount'),
      dataIndex: 'partialRefundAmount',
      key: 'partialRefundAmount',
      sorter: (a, b) => a.partialRefundAmount - b.partialRefundAmount,
      showSorterTooltip: false,
    },
    {
      title: t('amountFromExtra'),
      dataIndex: 'amountFromExtra',
      key: 'amountFromExtra',
      sorter: (a, b) => a.amountFromExtra - b.amountFromExtra,
      showSorterTooltip: false,
    },
    {
      title: t('customerCollectedAmount'),
      dataIndex: 'customerCollectedAmount',
      key: 'customerCollectedAmount',
      sorter: (a, b) => a.customerCollectedAmount - b.customerCollectedAmount,
      showSorterTooltip: false,
    },
    {
      title: t('courierCoordinateTimeOfDelivery'),
      dataIndex: 'latitudeOnDelivery',
      key: 'latitudeOnDelivery',
      sorter: (a, b) => a.latitudeOnDelivery - b.latitudeOnDelivery,
      showSorterTooltip: false,
      render: (text, record) =>
        record.latitudeOnDelivery && record.longitudeOnDelivery
          ? `${record.latitudeOnDelivery.toFixed(8)}, ${record.longitudeOnDelivery.toFixed(8)}`
          : '--',
    },
    {
      title: t('coordinatesOfCustomer'),
      dataIndex: 'customerLatitude',
      key: 'customerLatitude',
      sorter: (a, b) => a.customerLatitude - b.customerLatitude,
      render: (text, record) =>
        record.customerLatitude && record.customerLongitude
          ? `${record.customerLatitude.toFixed(8)}, ${record.customerLongitude.toFixed(8)}`
          : '--',
    },
    {
      title: t('collectorFullName'),
      dataIndex: 'collectorFullName',
      key: 'collectorFullName',
      sorter: (a, b) => a.collectorFullName?.localeCompare(b.collectorFullName),
    },
    {
      title: t('courierFullName'),
      dataIndex: 'courierFullName',
      key: 'courierFullName',
      sorter: (a, b) => a.courierFullName?.localeCompare(b.courierFullName),
    },
    {
      title: t('customerType'),
      dataIndex: 'customerType',
      key: 'customerType',
      sorter: (a, b) => a.customerType?.localeCompare(b.customerType),
    },
  ];

  const fetchCSVData = async () => {
    setExportButtonLoading(true);
    try {
      const orderStatusReportRes = await getOrderStatusReport(
        '&size=10000',
        payloadData,
      );
      const orderStatusReportData = orderStatusReportRes.data.content;

      if (orderStatusReportData) {
        const csvData = [
          CSV_EXPORT_COLUMNS_ORDER_STATUS_REPORT.map((item) => t(item)),
          ...orderStatusReportData.map((item) => [
            item.customerOrderNumber,
            item.platform,
            item.deliveryDate ? item.deliveryDate : '--',
            item.regionName,
            item.storeCode,
            item.storeName,
            item.customerFullName,
            item.customerId,
            item.customerPhoneNumber,
            t(item.orderStatus),
            item.cancelReturnStatus,
            item.cancellationReason,
            item.nonDeliveryReason,
            item.paymentType,
            item.storeStatus,
            item.orderDeliveryDate,
            item.timeSlot,
            item.collectionStartDate
              ? moment
                .utc(item.collectionStartDate)
                .add(3, 'hours')
                .format('DD-MM-YYYY HH:mm')
              : '--',
            item.collectionEndDate
              ? moment
                .utc(item.collectionEndDate)
                .add(3, 'hours')
                .format('DD-MM-YYYY HH:mm')
              : '--',
            item.collectionTime ? item.collectionTime : '--',
            item.courierDeliveryDate
              ? moment
                .utc(item.courierDeliveryDate)
                .add(3, 'hours')
                .format('DD-MM-YYYY HH:mm')
              : '--',
            item.customerDeliveryDate
              ? moment
                .utc(item.customerDeliveryDate)
                .add(3, 'hours')
                .format('DD-MM-YYYY HH:mm')
              : '--',
            item.deliveryTime ?? '--',
            item.price,
            item.refundedPrice,
            item.refundAmountForUnavailableProduct,
            item.partialRefundAmount,
            item.amountFromExtra,
            item.customerCollectedAmount,
            item.latitudeOnDelivery && item.longitudeOnDelivery
              ? `${item.latitudeOnDelivery.toFixed(8)}, ${item.longitudeOnDelivery.toFixed(8)}`
              : '--',
            item.customerLatitude && item.customerLongitude
              ? `${item.customerLatitude.toFixed(8)}, ${item.customerLongitude.toFixed(8)}`
              : '--',
            item.collectorFullName,
            item.courierFullName,
            item.customerType
          ]),
        ];
        setDownloadedData(csvData);
        csvLink.current.link.click();
      }
    } finally {
      setExportButtonLoading(false);
    }
  };

  const datepickerChangeHandler = (dates, datesString) => {
    const startDate = datesString[0] + ' 00:00';
    const endDate = datesString[1] + ' 23:59';

    setDates([startDate, endDate]);
    startDate && endDate ? setButtonDisabled(false) : setButtonDisabled(true);
  };

  return (
    <>
      <Card
        actions={[
          <Button
            disabled={buttonDisabled}
            loading={loading}
            onClick={() => form.submit()}
            type="primary"
          >
            {t('list')}
          </Button>,
        ]}
      >
        {
          <Form
            layout="inline"
            form={form}
            wrapperCol={{ span: 18 }}
            name="basic"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Row gutter={24} style={{ width: '100%' }}>
              <Col span={userRegions.length > 0 ? 6 : 8}>
                <Form.Item
                  name="dateRange"
                  width="100%"
                  label={t('orderDeliveryDateRange')}
                  labelAlign="left"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || value.length !== 2) {
                          return Promise.resolve();
                        }
                        const startDate = value[0];
                        const endDate = value[1];
                        const maxDays = selectedStores.length > 0 ? 7 : 2;
                        const maxAllowedDate = moment(startDate).add(maxDays, 'days');
                        if (endDate && endDate.isAfter(maxAllowedDate)) {
                          return Promise.reject({

                            message: selectedStores.length ? t('maxSelectedDateRangeSevenDays') : t('maxSelectedDateRangeOneDayOrSelectCity'),
                          });
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  wrapperCol={{ span: 24 }}
                >
                  <DatePicker.RangePicker
                    placeholder={[t('validityStartDate'), t('validityEndDate')]}
                    style={{ width: '100%' }}
                    allowClear={false}
                    format={'YYYY-MM-DD'}
                    onChange={datepickerChangeHandler}
                  />
                </Form.Item>
              </Col>
              {userRegions.length > 0 && (
                <Col span={6}>
                  <Form.Item
                    name="regionIds"
                    width="100%"
                    label={t('region')}
                    labelAlign="left"
                    wrapperCol={{ span: 24 }}
                  >
                    <Select
                      className="fullWidth"
                      mode="multiple"
                      showSearch
                      placeholder={t('selectRegion')}
                      onChange={handleChangeRegion}
                      maxTagCount={1}
                      style={{ width: '100%' }}
                      filterOption={(input, option) =>
                        option.children &&
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      optionFilterProp="value"
                      value={selectedRegions}
                      labelInValue
                    >
                      {userRegions &&
                        userRegions.map((region) => (
                          <Option key={region.id} value={region.id}>
                            {region.title}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col span={userRegions.length > 0 ? 6 : 8}>
                <Form.Item
                  name="storeIds"
                  width="100%"
                  label={t('store')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    className="fullWidth"
                    showSearch
                    filterOption={true}
                    optionFilterProp="children"
                    placeholder={t('selectStore')}
                    onChange={handleChangeStore}
                    style={{ width: '100%' }}
                    mode="multiple"
                    maxTagCount={1}
                    value={selectedStores}
                    disabled={
                      userRegions.length > 0 && selectedRegions.length <= 0
                    }
                  >
                    {stores &&
                      stores.map((store) => (
                        <Option key={store.id} value={store.id}>
                          {store.title || store.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={userRegions.length > 0 ? 6 : 8}>
                <Form.Item
                  name="platform"
                  width="100%"
                  label={t('orderChannel')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    showSearch
                    placeholder={t('select')}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      option.value &&
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    optionFilterProp="value"
                    allowClear={true}
                  >
                    <Option key={'SOK'} value={'SOK'}>
                      {'ŞOK'}
                    </Option>
                    <Option key={'ISTEGELSIN'} value={'ISTEGELSIN'}>
                      {'İsteGelsin'}
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24} style={{ width: '100%' }}>
              <Col span={6}>
                <Form.Item
                  name="orderStatus"
                  width="100%"
                  label={t('orderStatus')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    showSearch
                    placeholder={t('select')}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      option.value &&
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    optionFilterProp="value"
                    allowClear={true}
                  >
                    {Object.keys(ORDER_STATUSES).map((status) => (
                      <Option key={status} value={t(status)}>
                        {t(status)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="orderNo"
                  width="100%"
                  label={t('orderNo')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Input placeholder={t('orderNo')} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="customerName"
                  width="100%"
                  label={t('customerFullName')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                >
                  <Input placeholder={t('fullName')} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="customerPhoneNumber"
                  width="100%"
                  label={t('customerPhoneNumber')}
                  labelAlign="left"
                  wrapperCol={{ span: 24 }}
                  rules={[
                    {
                      message: t('alert.notValidPhone'),
                      pattern: new RegExp(/^[0-9]+$/),
                    },
                  ]}
                >
                  <Input placeholder={'5xx...'} maxLength={10} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        }
      </Card>
      {orderStatusReport && (
        <>
          <div style={{ margin: '5px' }}>
            <Button
              style={{ marginRight: '3px' }}
              shape="round"
              icon={<DownloadOutlined />}
              onClick={fetchCSVData}
            ></Button>
            <CSVLink
              filename={'OrderStatusReport'}
              data={downloadedData}
              ref={csvLink}
              className="hidden"
            />
          </div>
          <ConfigProvider locale={trTR}>
            <Table
              loading={loading}
              size="small"
              style={{ width: '100%' }}
              columns={columns}
              dataSource={orderStatusReport.map((item, index) => ({
                ...item,
                key: index.toString(),
              }))}
              pagination={tableParams.pagination}
              onChange={handleTableChange}
              scroll={{
                y: '70vh',
                x: '4000px',
              }}
            />
          </ConfigProvider>
        </>
      )}
    </>
  );
};

export default Index;
